import React from 'react'
import { connect } from 'react-redux'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { ActionIcon, TicketFooter } from 'view/shared/ticket'
import { ButtonStrip, TextButton, TextButtonType } from 'view/components/buttons'
import { DialogRow } from 'view/components/inputs'
import { DealActions, DealDetail, DealStatus, ServicingType } from 'api'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { iconSet } from 'view/components/icons'
import {
  canCancel,
  canClearingFailureManuallyCancel,
  canClearingFailureRetry,
  canClearingFailureManuallyComplete,
  canServicingAccept,
  canServicingStartAmend,
  canServicingRevertAttributes,
  canServicingCancel
} from '../utils'
import { ActionItem, SplitButtonDropdown } from 'view/components/menus'

export interface DealTicketFooterProps extends ModalConsumerProps {
  dealDetail: DealDetail
  showCloseButton?: boolean
  onClose?: () => unknown
  onCancel: (dealId: string) => unknown
  onServicingStartAmend: typeof DealActions.servicingStartAmend
  onServicingCancelAmend: typeof DealActions.servicingCancel
  onServicingAccept: typeof DealActions.servicingAccept
  onClearingFailureManuallyCancel: (dealId: string) => unknown
  onClearingFailureRetry: (dealId: string) => unknown
  onClearingFailureManuallyComplete: (dealId: string) => unknown
  isAdminAmendAndWithOtherUser: boolean
}

const mapDispatchToProps = {
  onCancel: DealActions.cancel,
  onServicingStartAmend: DealActions.servicingStartAmend,
  onServicingCancelAmend: DealActions.servicingCancel,
  onServicingAccept: DealActions.servicingAccept,
  onClearingFailureManuallyCancel: DealActions.clearingFailureManuallyCancel,
  onClearingFailureRetry: DealActions.clearingFailureRetry,
  onClearingFailureManuallyComplete: DealActions.clearingFailureManuallyComplete
}

const renderActions = (actions: ActionItem[]) => {
  if (actions.length === 0) {
    return null
  }

  // retain cancel icon if this is the only button visible - we may want to consider incorporating icons into the split button dropdown
  if (actions.length === 1) {
    return (
      <TextButton type={TextButtonType.Tertiary} onClick={actions[0].onClick} dataTestId={'DealTicketFooter-TextButton-ActionCancelTrade'}>
        {actions[0].label === Locale.translate(Translation.TC_ACTION_CANCEL_TRADE) ? (
          <ActionIcon
            icon={iconSet.noEntryIcon}
            label={Locale.translate(Translation.TC_ACTION_CANCEL_TRADE)}
            labelPosition={constants.LabelPosition.Right}
            isCancel={true}
          />
        ) : (
          actions[0].label
        )}
      </TextButton>
    )
  }

  return <SplitButtonDropdown actions={actions} autoHideEllipsis={true} maxButtons={1} />
}

export class DealTicketFooterComponent extends React.Component<DealTicketFooterProps> {
  render() {
    const { dealDetail, onServicingStartAmend, showCloseButton, isAdminAmendAndWithOtherUser } = this.props
    const isAmending = dealDetail.deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_AMEND

    const actions = this.getActions()

    return (
      <TicketFooter>
        <DialogRow horizontalLayout={constants.HorizontalLayout.SpaceBetween}>
          <ButtonStrip spacing="5px">
            {(showCloseButton || dealDetail.deal.status === DealStatus.CANCELLED) && (
              <TextButton
                type={TextButtonType.Tertiary}
                onClick={this.props.onClose}
                dataTestId={'DealTicketFooter-TextButton-ButtonCloseTicket'}>
                {Locale.translate(Translation.TC_BUTTON_CLOSE_TICKET)}
              </TextButton>
            )}
            {renderActions(actions)}
          </ButtonStrip>
          <ButtonStrip spacing="5px">
            {canServicingStartAmend(dealDetail) && (
              <TextButton
                type={TextButtonType.Tertiary}
                onClick={() => onServicingStartAmend(dealDetail.deal.dealId)}
                dataTestId={'DealTicketFooter-TextButton-ActionStartAmend'}>
                {Locale.translate(Translation.TC_ACTION_START_AMEND)}
              </TextButton>
            )}
            {isAmending && canServicingCancel(dealDetail) && (
              <TextButton
                type={TextButtonType.Tertiary}
                onClick={isAdminAmendAndWithOtherUser ? this.handleForceServicingWithdraw : this.handleServicingWithdraw}
                dataTestId={'DealTicketFooter-TextButton-ActionWithdraw'}>
                {Locale.translate(isAdminAmendAndWithOtherUser ? Translation.TC_ACTION_FORCE_STOP_AMEND : Translation.TC_ACTION_STOP_AMEND)}
              </TextButton>
            )}
            {!isAdminAmendAndWithOtherUser && isAmending && canServicingAccept(dealDetail) && (
              <TextButton
                type={TextButtonType.Good}
                onClick={this.handleServicingAccept}
                dataTestId={'DealTicketFooter-TextButton-ActionAmend'}>
                {Locale.translate(Translation.TC_ACTION_AMEND)}
              </TextButton>
            )}
          </ButtonStrip>
        </DialogRow>
      </TicketFooter>
    )
  }

  private getActions = (): ActionItem[] => {
    const { dealDetail, onCancel, onClearingFailureManuallyCancel, onClearingFailureRetry, onClearingFailureManuallyComplete } = this.props

    const actions = [
      {
        label: Locale.translate(Translation.TC_ACTION_CANCEL_TRADE),
        onClick: this.createConfirmationHandler(Translation.TC_CANCEL_DEAL, Translation.SC_CANCEL_DEAL_CONFIRMATION, onCancel),
        enabled: canCancel(dealDetail)
      },
      {
        label: Locale.translate(Translation.TC_ACTION_CANCEL_TRADE),
        onClick: this.createConfirmationHandler(
          Translation.TC_CANCEL_DEAL,
          Translation.SC_CANCEL_DEAL_CONFIRMATION,
          onClearingFailureManuallyCancel
        ),
        enabled: canClearingFailureManuallyCancel(dealDetail)
      },
      {
        label: Locale.translate(Translation.TC_ACTION_RETRY_TRADE),
        onClick: this.createConfirmationHandler(Translation.TC_RETRY_DEAL, Translation.SC_RETRY_DEAL_CONFIRMATION, onClearingFailureRetry),
        enabled: canClearingFailureRetry(dealDetail)
      },
      {
        label: Locale.translate(Translation.TC_ACTION_COMPLETE_TRADE),
        onClick: this.createConfirmationHandler(
          Translation.TC_COMPLETE_DEAL,
          Translation.SC_COMPLETE_DEAL_CONFIRMATION,
          onClearingFailureManuallyComplete
        ),
        enabled: canClearingFailureManuallyComplete(dealDetail)
      }
    ]

    return actions.filter(action => action.enabled)
  }

  private createConfirmationHandler = (title: Translation, message: Translation, handler: (dealId: string) => unknown) => async () => {
    const { showDialog, dealDetail } = this.props
    const dialogArgs = {
      type: NotificationType.Confirm,
      title,
      message,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }
    const dialogResult = await showDialog(dialogArgs)

    if (dialogResult === Translation.TC_BUTTON_YES) {
      handler(dealDetail.deal.dealId)
    }
  }

  private handleServicingWithdraw = async () => {
    const { dealDetail, onServicingCancelAmend, showDialog } = this.props
    if (canServicingRevertAttributes(dealDetail)) {
      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_STOP_AMEND_DEAL,
        message: Translation.SC_CLOSE_TICKET_WITH_AMENDMENTS,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }
      const dialogResult = await showDialog(dialogArgs)

      if (dialogResult === Translation.TC_BUTTON_YES) {
        onServicingCancelAmend(dealDetail.deal.dealId)
      }
    } else {
      onServicingCancelAmend(dealDetail.deal.dealId)
    }
  }

  private handleForceServicingWithdraw = async () => {
    const { dealDetail, onServicingCancelAmend, showDialog } = this.props
    const dialogArgs = {
      type: NotificationType.Confirm,
      title: Translation.TC_FORCE_STOP_AMEND_DEAL,
      message: Translation.SC_FORCE_STOP_AMEND_TICKET,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }
    const dialogResult = await showDialog(dialogArgs)

    if (dialogResult === Translation.TC_BUTTON_YES) {
      onServicingCancelAmend(dealDetail.deal.dealId)
    }
  }

  private handleServicingAccept = async () => {
    const { onServicingAccept, showDialog, dealDetail } = this.props

    const dialogArgs = {
      type: NotificationType.Confirm,
      title: Translation.TC_AMEND_DEAL,
      message: Translation.SC_AMEND_DEAL_CONFIRMATION,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }
    const dialogResult = await showDialog(dialogArgs)

    if (dialogResult === Translation.TC_BUTTON_YES) {
      onServicingAccept(dealDetail.deal.dealId)
    }
  }
}

export const DealTicketFooter = withModal(connect(null, mapDispatchToProps)(DealTicketFooterComponent))
